import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { fmtPrice, fmtPct, pctClass } from "@/lib/format";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Watchlist() {
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [adding, setAdding] = useState(false);
  const [allSymbols, setAllSymbols] = useState([]);
  const prev = useRef({});

  const loadList = async () => {
    try {
      const r = await api.get("/watchlist");
      setItems(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    if (!adding) return;
    api.get("/market/symbols").then((r) => setAllSymbols(r.data));
  }, [adding]);

  // Poll quotes
  useEffect(() => {
    let active = true;
    const tick = async () => {
      if (!items.length) return;
      try {
        const r = await api.get("/market/quotes", { params: { symbols: items.map(i => i.symbol).join(",") } });
        if (!active) return;
        const map = {};
        r.data.forEach(q => { map[q.symbol] = q; });
        setQuotes(map);
      } catch (e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { active = false; clearInterval(id); };
  }, [items]);

  const addSymbol = async (s) => {
    try {
      await api.post("/watchlist", { symbol: s.symbol, asset_class: s.asset_class });
      toast.success(`${s.symbol} added`);
      setAdding(false);
      loadList();
    } catch (e) { toast.error("Failed to add"); }
  };

  const remove = async (sym) => {
    await api.delete(`/watchlist/${sym}`);
    loadList();
  };

  return (
    <div className="panel" data-testid="watchlist-panel">
      <div className="panel-header">
        <span>Watchlist</span>
        <button
          className="text-[10px] hover:text-[#00E5FF] flex items-center gap-1"
          onClick={() => setAdding(true)}
          data-testid="watchlist-add-btn"
        >
          <Plus size={11} weight="bold" /> Add
        </button>
      </div>
      <div className="max-h-[320px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-4 text-xs text-[#4B5563] text-center">Empty. Add symbols to track.</div>
        ) : items.map(it => {
          const q = quotes[it.symbol];
          const pkey = it.symbol + ":lastprice";
          let flash = "";
          if (q) {
            const last = prev.current[pkey];
            if (last !== undefined) {
              if (q.price > last) flash = "flash-up";
              else if (q.price < last) flash = "flash-down";
            }
            prev.current[pkey] = q.price;
          }
          return (
            <div key={it.id} className={`px-3 py-2 border-b border-[#272A30] flex items-center justify-between gap-2 ${flash}`} data-testid={`wl-row-${it.symbol}`}>
              <div className="flex flex-col">
                <span className="text-xs font-medium">{it.symbol}</span>
                <span className="text-[9px] uppercase tracking-[0.18em] text-[#4B5563] font-mono">{it.asset_class}</span>
              </div>
              <div className="flex flex-col items-end font-mono text-xs flex-1">
                <span>{q ? fmtPrice(q.price, q.price < 10 ? 4 : 2) : "—"}</span>
                <span className={`text-[10px] ${q ? pctClass(q.change_pct_24h) : ""}`}>{q ? fmtPct(q.change_pct_24h) : "—"}</span>
              </div>
              <button
                onClick={() => remove(it.symbol)}
                className="text-[#4B5563] hover:text-[#FF3B30]"
                data-testid={`wl-remove-${it.symbol}`}
              >
                <X size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {adding && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.85)" }} data-testid="watchlist-add-modal">
          <div className="panel w-[420px] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold tracking-tight uppercase">Add Symbol</h3>
              <button onClick={() => setAdding(false)} className="text-[#8B95A5]" data-testid="watchlist-add-close"><X size={14}/></button>
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              {allSymbols.map(s => {
                const inWl = items.some(i => i.symbol === s.symbol);
                return (
                  <button
                    key={s.symbol}
                    onClick={() => !inWl && addSymbol(s)}
                    disabled={inWl}
                    className="w-full px-3 py-2 border-b border-[#272A30] flex items-center justify-between hover:bg-[#1A1D24] disabled:opacity-30"
                    data-testid={`wl-pick-${s.symbol}`}
                  >
                    <div className="text-left">
                      <div className="text-xs">{s.symbol}</div>
                      <div className="text-[10px] text-[#8B95A5]">{s.name}</div>
                    </div>
                    <span className="tag">{s.asset_class}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
